/* __next_internal_client_entry_do_not_use__ PDFConverter auto */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { CheckCircleIcon, DownloadIcon, FileTextIcon } from "__barrel_optimize__?names=CheckCircleIcon,DownloadIcon,FileTextIcon!=!lucide-react";
import { useState } from 'react';
import { toast } from 'sonner';
import { FileUpload } from './FileUpload';
export const PDFConverter = ()=>{
    const [conversion, setConversion] = useState({
        status: 'idle',
        progress: 0
    });
    const handleFileSelect = async (file)=>{
        setConversion({
            status: 'uploading',
            progress: 0,
            fileName: file.name,
            message: 'Preparing upload...'
        });
        try {
            var _response_body;
            // Create FormData for single file upload
            const formData = new FormData();
            formData.append('file', file);
            // Use the streaming endpoint
            const response = await fetch('/api/conversions/stream', {
                method: 'POST',
                body: formData
            });
            if (!response.ok) {
                const errorData = await response.json().catch(()=>({}));
                throw new Error(errorData.error || "HTTP ".concat(response.status, ": ").concat(response.statusText));
            }
            // Handle streaming response
            const reader = (_response_body = response.body) === null || _response_body === void 0 ? void 0 : _response_body.getReader();
            const decoder = new TextDecoder();
            if (!reader) {
                throw new Error('Failed to get response reader');
            }
            let buffer = '';
            let isSSEMode = true;
            const binaryChunks = [];
            setConversion((prev)=>({
                    ...prev,
                    status: 'converting',
                    message: 'Starting conversion...'
                }));
            while(true){
                const { done, value } = await reader.read();
                if (done) break;
                if (isSSEMode) {
                    // Process SSE data
                    buffer += decoder.decode(value, {
                        stream: true
                    });
                    const lines = buffer.split('\n');
                    buffer = lines.pop() || ''; // Keep incomplete line in buffer
                    for (const line of lines){
                        if (line.startsWith('data: ')) {
                            const data = line.slice(6); // Remove 'data: ' prefix
                            if (data.trim()) {
                                try {
                                    const parsed = JSON.parse(data);
                                    if (parsed.type === 'init') {
                                        setConversion((prev)=>({
                                                ...prev,
                                                message: parsed.message,
                                                progress: 5
                                            }));
                                    } else if (parsed.type === 'progress') {
                                        console.log('Progress update:', parsed); // Debug logging
                                        setConversion((prev)=>({
                                                ...prev,
                                                progress: Math.min(parsed.percentage || 0, 100),
                                                message: parsed.message,
                                                currentPage: parsed.current_page,
                                                totalPages: parsed.total_pages
                                            }));
                                    } else if (parsed.type === 'complete' || parsed.type === 'completion') {
                                        setConversion((prev)=>({
                                                ...prev,
                                                message: parsed.message,
                                                progress: 95,
                                                currentPage: parsed.current_page,
                                                totalPages: parsed.total_pages
                                            }));
                                    } else if (parsed.type === 'file_info') {
                                        setConversion((prev)=>({
                                                ...prev,
                                                message: "Preparing download: ".concat(parsed.filename)
                                            }));
                                    } else if (parsed.type === 'error') {
                                        throw new Error(parsed.message);
                                    }
                                } catch (e) {
                                    console.warn('Failed to parse SSE data:', data);
                                }
                            }
                        } else if (line.startsWith('event: end_sse')) {
                            // Switch to binary mode
                            isSSEMode = false;
                            break;
                        }
                    }
                } else {
                    // Collect binary data
                    binaryChunks.push(value);
                }
            }
            // Create blob from binary chunks
            if (binaryChunks.length > 0) {
                const blob = new Blob(binaryChunks, {
                    type: 'application/zip'
                });
                const url = URL.createObjectURL(blob);
                setConversion((prev)=>({
                        ...prev,
                        status: 'completed',
                        progress: 100,
                        downloadUrl: url,
                        message: 'Conversion completed successfully!'
                    }));
                toast.success("Conversion Complete", {
                    description: "PDF successfully converted to JRXML format."
                });
            } else {
                throw new Error('No file data received');
            }
        } catch (error) {
            console.error('Conversion error:', error);
            setConversion((prev)=>({
                    ...prev,
                    status: 'error',
                    error: error instanceof Error ? error.message : 'Unknown error occurred',
                    message: 'Conversion failed'
                }));
            toast.error("Conversion Failed", {
                description: error instanceof Error ? error.message : 'An unexpected error occurred during conversion.'
            });
        }
    };
    const handleDownload = ()=>{
        if (conversion.downloadUrl) {
            const link = document.createElement('a');
            link.href = conversion.downloadUrl;
            link.download = 'converted_jrxml.zip';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };
    const resetConverter = ()=>{
        if (conversion.downloadUrl) {
            URL.revokeObjectURL(conversion.downloadUrl);
        }
        setConversion({
            status: 'idle',
            progress: 0
        });
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "w-full max-w-2xl mx-auto space-y-6",
        children: [
            conversion.status === 'idle' && /*#__PURE__*/ _jsx(FileUpload, {
                onFileSelect: handleFileSelect
            }),
            conversion.status !== 'idle' && /*#__PURE__*/ _jsx(Card, {
                className: "p-6",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-6",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center gap-3",
                            children: [
                                /*#__PURE__*/ _jsx(FileTextIcon, {
                                    className: "w-8 h-8 text-primary"
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "font-semibold text-foreground",
                                            children: conversion.fileName
                                        }),
                                        /*#__PURE__*/ _jsxs("p", {
                                            className: "text-sm text-muted-foreground",
                                            children: [
                                                conversion.status === 'uploading' && (conversion.message || 'Uploading file...'),
                                                conversion.status === 'converting' && /*#__PURE__*/ _jsxs("span", {
                                                    children: [
                                                        conversion.message || 'Converting to JRXML...',
                                                        conversion.currentPage && conversion.totalPages && /*#__PURE__*/ _jsxs("span", {
                                                            className: "ml-2 text-xs bg-secondary/50 px-2 py-1 rounded",
                                                            children: [
                                                                "Page ",
                                                                conversion.currentPage,
                                                                " of ",
                                                                conversion.totalPages
                                                            ]
                                                        })
                                                    ]
                                                }),
                                                conversion.status === 'completed' && (conversion.message || 'Conversion completed!'),
                                                conversion.status === 'error' && 'Conversion failed'
                                            ]
                                        })
                                    ]
                                }),
                                conversion.status === 'completed' && /*#__PURE__*/ _jsx(CheckCircleIcon, {
                                    className: "w-6 h-6 text-success ml-auto"
                                }),
                                conversion.status === 'error' && /*#__PURE__*/ _jsx("div", {
                                    className: "ml-auto text-destructive",
                                    children: /*#__PURE__*/ _jsx("svg", {
                                        className: "w-6 h-6",
                                        fill: "none",
                                        stroke: "currentColor",
                                        viewBox: "0 0 24 24",
                                        children: /*#__PURE__*/ _jsx("path", {
                                            strokeLinecap: "round",
                                            strokeLinejoin: "round",
                                            strokeWidth: 2,
                                            d: "M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                        })
                                    })
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-3",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex justify-between text-sm",
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "text-muted-foreground",
                                            children: "Progress"
                                        }),
                                        /*#__PURE__*/ _jsxs("span", {
                                            className: "font-semibold text-lg text-primary",
                                            children: [
                                                Math.round(conversion.progress),
                                                "%"
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(Progress, {
                                    value: conversion.progress,
                                    className: "w-full h-4"
                                }),
                                conversion.currentPage && conversion.totalPages && /*#__PURE__*/ _jsxs("div", {
                                    className: "text-sm text-center font-medium",
                                    children: [
                                        /*#__PURE__*/ _jsxs("span", {
                                            className: "text-primary",
                                            children: [
                                                "Page ",
                                                conversion.currentPage
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "text-muted-foreground",
                                            children: " of "
                                        }),
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "text-primary",
                                            children: conversion.totalPages
                                        })
                                    ]
                                })
                            ]
                        }),
                        conversion.status === 'completed' && /*#__PURE__*/ _jsxs("div", {
                            className: "flex gap-3",
                            children: [
                                /*#__PURE__*/ _jsxs(Button, {
                                    onClick: handleDownload,
                                    className: "flex-1",
                                    children: [
                                        /*#__PURE__*/ _jsx(DownloadIcon, {
                                            className: "w-4 h-4 mr-2"
                                        }),
                                        "Download ZIP"
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(Button, {
                                    variant: "outline",
                                    onClick: resetConverter,
                                    children: "Convert Another"
                                })
                            ]
                        }),
                        conversion.status === 'error' && /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-3",
                            children: [
                                conversion.error && /*#__PURE__*/ _jsx("div", {
                                    className: "p-3 rounded-md bg-destructive/10 border border-destructive/20",
                                    children: /*#__PURE__*/ _jsx("p", {
                                        className: "text-sm text-destructive",
                                        children: conversion.error
                                    })
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex gap-3",
                                    children: [
                                        /*#__PURE__*/ _jsx(Button, {
                                            variant: "outline",
                                            onClick: resetConverter,
                                            className: "flex-1",
                                            children: "Try Again"
                                        }),
                                        /*#__PURE__*/ _jsx(Button, {
                                            variant: "outline",
                                            onClick: resetConverter,
                                            children: "Start Over"
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
};
